// ─── Impostazioni → Regole per i soci → «Promoter» ──────────────────────────
//
// 16/10/2026, mockup «Promoter», approvato: *«vai, tutte sì»*. Dal committente:
// *«Dentro regole per i soci mi devi costruire una sottosezione che si chiama
// promoter, dove io posso creare delle regole a determinati soci che sono in
// anagrafica … questi soci non pagano il campo»*. E lo stesso giorno: niente
// giorni né orari a pagamento; *«se sono presenti due promoter nella stessa
// partita, pagano la quota normale, tutti quanti»*; la spunta «Staff» per chi
// non paga mai e non rientra nel minimo; in calendario le sigle PR e ST.
//
// Due riquadri: ① le regole e ② la lista dei promoter, con il minimo di partite
// di ognuno, la settimana, il risparmio, la spunta «Staff» e «Togli».
//
// 🎾 18/10/2026, mockup «Minimo per promoter», approvato: 🗣️ *«Non c'è una regola
// minima di partenza … lo devo poter settare per ogni singolo promoter»*. Il
// numero per tutti non c'è più: ogni promoter ha il suo, scritto quando lo si
// aggiunge e cambiato con «Cambia», e vale da subito.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, type ElencoSoci, type PaginaPromoter, type PromoterInElenco } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Campo, Guasto } from '../../components/pezzi.js';
import { SoloLettura, usaModifica } from './comune.js';

const CHIAVE = 'promoter';

/** Oggi, giorno di Roma. */
const oggi = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(new Date());

/** «16/10/2026», con l'anno: come nella scheda del socio. */
const giorno = (d: string) => d.split('-').reverse().join('/');

export function Promoter(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const letta = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<PaginaPromoter>('GET', '/settings/promoter'),
  });
  if (letta.isLoading) return <Attesa cosa="i promoter" />;
  if (letta.error || !letta.data) return <Guasto errore={letta.error} />;
  return (
    <>
      <Regole modificabile={modificabile} />
      <Elenco pagina={letta.data} modificabile={modificabile} />
    </>
  );
}

function Regole({ modificabile }: { modificabile: boolean }) {
  return (
    <section>
      <header className="testata">
        <h3>Promoter</h3>
        <SoloLettura mostra={modificabile} />
      </header>
      <div className="bot-soci">
        <fieldset className="riquadro">
          <h4>Le regole</h4>
          <ul className="piccolo">
            <li>
              Il promoter <strong>non paga la quota campo</strong>, a qualunque giorno e ora.
            </li>
            <li>
              Con <strong>due o più promoter</strong> nella stessa partita, pagano tutti la quota del listino.
              Se ne resta uno solo, torna gratis.
            </li>
            <li>
              Ogni promoter ha il <strong>suo minimo di partite a settimana</strong>: lo scrivi quando lo
              aggiungi, e lo cambi nella lista qui sotto. Vale da subito, anche per la settimana in corso.
            </li>
            <li>
              Lo <strong>staff</strong> (sigla ST) non paga mai, non conta fra i promoter in campo e non ha il
              minimo di partite.
            </li>
          </ul>
          <p className="tenue piccolo">
            Conta una partita non annullata e già giocata, anche quando ha pagato la quota. Giovedì alle 10
            chi è indietro riceve un avviso; domenica alle 20 tutti ricevono il riepilogo con il risparmio.
            Dopo 2 settimane di fila sotto il minimo, la Segreteria lo trova in Segnalazioni.
          </p>
        </fieldset>
      </div>
    </section>
  );
}

/** 🎾 Il minimo scritto: un intero da 1 a 14, o `null`. */
const minimoLetto = (t: string): number | null => {
  const n = Number(t);
  return t.trim() !== '' && Number.isInteger(n) && n >= 1 && n <= 14 ? n : null;
};

/** 🎾 Il minimo da cambiare sulla riga (anche togliendo la spunta «Staff» a chi non l'aveva). */
function MinimoSullaRiga({
  minimo,
  inCorso,
  onSalva,
  onAnnulla,
}: {
  minimo: number | null;
  inCorso: boolean;
  onSalva: (n: number) => void;
  onAnnulla: () => void;
}) {
  const [testo, setTesto] = useState(minimo === null ? '' : String(minimo));
  const n = minimoLetto(testo);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (n !== null) onSalva(n);
      }}
    >
      <input
        type="number"
        min={1}
        max={14}
        className="numero-corto"
        aria-label="Minimo di partite a settimana"
        value={testo}
        onChange={(e) => setTesto(e.target.value)}
      />{' '}
      <button type="submit" className="piccolo" disabled={n === null || inCorso}>
        Salva
      </button>{' '}
      <button type="button" className="bordato piccolo" onClick={onAnnulla}>
        Annulla
      </button>
      {testo !== '' && n === null && <p className="guasto">Un numero da 1 a 14.</p>}
    </form>
  );
}

function Settimana({ p }: { p: PromoterInElenco }) {
  if (p.stato === 'da_iniziare') return <span className="tenue">📅 comincia il {giorno(p.dal)}</span>;
  if (p.stato === 'finito') return <span className="tenue">📅 periodo finito</span>;
  if (p.stato === 'staff') return <span className="tenue">—</span>;
  if (p.stato === 'pausa') return <span className="tenue">⏸️ in pausa</span>;
  const segno = p.stato === 'pieno' ? '🟢' : p.stato === 'indietro' ? '🟡' : '⚪';
  return (
    <span>
      {segno} {p.giocate} su {p.minimo}
      {p.settimaneSotto > 0 && p.stato !== 'pieno' && (
        <span className="tenue piccolo"> · {p.settimaneSotto + 1}ª settimana sotto</span>
      )}
    </span>
  );
}

function Elenco({ pagina, modificabile }: { pagina: PaginaPromoter; modificabile: boolean }) {
  const qc = useQueryClient();
  const [toglie, setToglie] = useState<string | null>(null);
  const [aggiunge, setAggiunge] = useState(false);
  const aggiorna = (dopo: PaginaPromoter) => {
    qc.setQueryData([CHIAVE], dopo);
    setToglie(null);
    setAggiunge(false);
  };
  /** La riga col minimo aperto: per «Cambia», o per togliere la spunta «Staff» a chi non l'aveva. */
  const [cambiaMinimo, setCambiaMinimo] = useState<{ memberId: string; perStaff: boolean } | null>(null);
  const staff = useMutation({
    meta: {
      fatto: (_d: unknown, v: unknown) =>
        (v as { staff: boolean }).staff ? 'segnato come staff' : 'tolto dallo staff',
    },
    mutationFn: (v: { memberId: string; staff: boolean; minimo?: number }) =>
      chiama<PaginaPromoter>('PUT', `/promoter/${v.memberId}/staff`, { staff: v.staff, minimo: v.minimo }),
    onSuccess: (dopo) => {
      aggiorna(dopo);
      setCambiaMinimo(null);
    },
  });
  const minimo = useMutation({
    meta: { fatto: 'minimo del promoter cambiato' },
    mutationFn: (v: { memberId: string; minimo: number }) =>
      chiama<PaginaPromoter>('PUT', `/promoter/${v.memberId}/minimo`, { minimo: v.minimo }),
    onSuccess: (dopo) => {
      aggiorna(dopo);
      setCambiaMinimo(null);
    },
  });
  const togli = useMutation({
    meta: { fatto: 'tolto dai promoter' },
    mutationFn: (memberId: string) => chiama<PaginaPromoter>('DELETE', `/promoter/${memberId}`),
    onSuccess: aggiorna,
  });
  const aggiungi = useMutation({
    meta: { fatto: 'promoter aggiunto' },
    mutationFn: (v: { memberId: string } & NuovoPromoter) => chiama<PaginaPromoter>('POST', '/promoter', v),
    onSuccess: aggiorna,
  });
  const [cambiaPeriodo, setCambiaPeriodo] = useState<string | null>(null);
  const periodo = useMutation({
    meta: { fatto: 'periodo del promoter salvato' },
    mutationFn: (v: { memberId: string; dal: string; al: string | null }) =>
      chiama<PaginaPromoter>('PUT', `/promoter/${v.memberId}/periodo`, { dal: v.dal, al: v.al }),
    onSuccess: (dopo) => {
      aggiorna(dopo);
      setCambiaPeriodo(null);
    },
  });

  return (
    <section className="riquadro">
      <h3>
        I promoter · settimana dal {giorno(pagina.settimana.dal)} al {giorno(pagina.settimana.al)}
      </h3>
      {pagina.items.length === 0 ? (
        <p className="tenue piccolo">La lista è vuota: aggiungi il primo promoter dall’anagrafica.</p>
      ) : (
        <div className="tabella-scorre">
          <table className="tabella">
            <thead>
              <tr>
                <th>Socio</th>
                <th>Minimo</th>
                <th>Settimana</th>
                <th>Risparmio del mese</th>
                <th>Periodo</th>
                <th>Risparmio in tutto</th>
                <th>Staff</th>
                {modificabile && <th />}
              </tr>
            </thead>
            <tbody>
              {pagina.items.map((p) => (
                <tr key={p.memberId}>
                  <td>
                    {p.nome} <span className="etichetta-pr">{p.staff ? 'ST' : 'PR'}</span>
                  </td>
                  <td>
                    {cambiaMinimo?.memberId === p.memberId ? (
                      <MinimoSullaRiga
                        minimo={p.minimo}
                        inCorso={minimo.isPending || staff.isPending}
                        onSalva={(n) =>
                          cambiaMinimo.perStaff
                            ? staff.mutate({ memberId: p.memberId, staff: false, minimo: n })
                            : minimo.mutate({ memberId: p.memberId, minimo: n })
                        }
                        onAnnulla={() => setCambiaMinimo(null)}
                      />
                    ) : p.staff ? (
                      <span className="tenue">—</span>
                    ) : (
                      <>
                        {p.minimo}
                        {modificabile && (
                          <>
                            {' '}
                            <button
                              type="button"
                              className="bordato piccolo"
                              onClick={() => setCambiaMinimo({ memberId: p.memberId, perStaff: false })}
                            >
                              Cambia
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    <Settimana p={p} />
                  </td>
                  <td>{formatCents(p.risparmioMeseCents)}</td>
                  <td>
                    {cambiaPeriodo === p.memberId ? (
                      <Periodo
                        dal={p.dal}
                        al={p.al}
                        inCorso={periodo.isPending}
                        onSalva={(v) => periodo.mutate({ memberId: p.memberId, ...v })}
                        onAnnulla={() => setCambiaPeriodo(null)}
                      />
                    ) : (
                      <>
                        dal {giorno(p.dal)}
                        {p.al ? ` al ${giorno(p.al)}` : ''}
                        {modificabile && (
                          <>
                            {' '}
                            <button
                              type="button"
                              className="bordato piccolo"
                              onClick={() => setCambiaPeriodo(p.memberId)}
                            >
                              Cambia
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </td>
                  <td>{formatCents(p.risparmioTotaleCents)}</td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`${p.nome} è dello staff`}
                      checked={p.staff}
                      disabled={!modificabile || staff.isPending}
                      onChange={(e) =>
                        // Tornando promoter senza un minimo, il minimo si scrive prima, sulla riga.
                        !e.target.checked && p.minimo === null
                          ? setCambiaMinimo({ memberId: p.memberId, perStaff: true })
                          : staff.mutate({ memberId: p.memberId, staff: e.target.checked })
                      }
                    />
                  </td>
                  {modificabile && (
                    <td>
                      {toglie === p.memberId ? (
                        <>
                          <button
                            type="button"
                            className="piccolo"
                            disabled={togli.isPending}
                            onClick={() => togli.mutate(p.memberId)}
                          >
                            Sì, togli
                          </button>{' '}
                          <button type="button" className="bordato piccolo" onClick={() => setToglie(null)}>
                            Lascia stare
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="bordato piccolo"
                          onClick={() => setToglie(p.memberId)}
                        >
                          Togli
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Guasto errore={staff.error ?? minimo.error ?? togli.error ?? aggiungi.error ?? periodo.error} />
      {modificabile &&
        (aggiunge ? (
          <AggiungiPromoter
            gia={new Set(pagina.items.map((p) => p.memberId))}
            inCorso={aggiungi.isPending}
            onScelto={(memberId, v) => aggiungi.mutate({ memberId, ...v })}
            onAnnulla={() => setAggiunge(false)}
          />
        ) : (
          <button type="button" onClick={() => setAggiunge(true)}>
            ➕ Aggiungi un promoter
          </button>
        ))}
    </section>
  );
}

interface NuovoPromoter {
  staff: boolean;
  dal: string;
  al: string | null;
  minimo: number | null;
}

function AggiungiPromoter({
  gia,
  inCorso,
  onScelto,
  onAnnulla,
}: {
  gia: ReadonlySet<string>;
  inCorso: boolean;
  onScelto: (memberId: string, v: NuovoPromoter) => void;
  onAnnulla: () => void;
}) {
  const [q, setQ] = useState('');
  const [comeStaff, setComeStaff] = useState(false);
  const [dal, setDal] = useState(oggi());
  const [al, setAl] = useState('');
  const [minimo, setMinimo] = useState('');
  const periodoBuono = dal !== '' && (al === '' || al >= dal);
  const n = minimoLetto(minimo);
  const minimoBuono = comeStaff || n !== null;
  const soci = useQuery({
    queryKey: ['promoter-cerca', q],
    queryFn: () =>
      chiama<ElencoSoci>('GET', `/members?${new URLSearchParams({ q: q.trim(), limit: '8' }).toString()}`),
    enabled: q.trim().length >= 2,
  });
  const trovati = (soci.data?.items ?? []).filter((s) => !gia.has(s.id));
  return (
    <div className="riquadro">
      <Campo
        etichetta="Cerca il socio in anagrafica"
        placeholder="cognome o nome"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <label className="spunta-grande">
        <input type="checkbox" checked={comeStaff} onChange={(e) => setComeStaff(e.target.checked)} /> È dello
        staff (non paga mai, senza minimo di partite)
      </label>
      <div className="riga-campi">
        <label className="campo">
          <span>Promoter dal</span>
          <input type="date" value={dal} onChange={(e) => setDal(e.target.value)} />
        </label>
        <label className="campo">
          <span>al (vuoto = fino a nuovo avviso)</span>
          <input type="date" value={al} min={dal} onChange={(e) => setAl(e.target.value)} />
        </label>
      </div>
      {!comeStaff && (
        <>
          <Campo
            etichetta="Partite a settimana, almeno (da 1 a 14)"
            type="number"
            min={1}
            max={14}
            value={minimo}
            onChange={(e) => setMinimo(e.target.value)}
          />
          {minimo !== '' && !minimoBuono && <p className="guasto">Un numero da 1 a 14.</p>}
        </>
      )}
      {!periodoBuono && <p className="guasto">Il periodo finisce dopo che comincia.</p>}
      <ul className="storico">
        {trovati.map((s) => (
          <li key={s.id}>
            {s.displayName} {s.email ? `· ${s.email}` : ''}{' '}
            <button
              type="button"
              className="bordato piccolo"
              disabled={inCorso || !periodoBuono || !minimoBuono}
              onClick={() =>
                onScelto(s.id, {
                  staff: comeStaff,
                  dal,
                  al: al === '' ? null : al,
                  minimo: comeStaff ? null : n,
                })
              }
            >
              Aggiungi
            </button>
          </li>
        ))}
      </ul>
      {soci.data && trovati.length === 0 && <p className="tenue piccolo">Prova con meno lettere.</p>}
      <button type="button" className="bordato piccolo" onClick={onAnnulla}>
        Lascia stare
      </button>
    </div>
  );
}

/** 📅 Il periodo dal… al…, da cambiare sulla riga. */
function Periodo({
  dal: dal0,
  al: al0,
  inCorso,
  onSalva,
  onAnnulla,
}: {
  dal: string;
  al: string | null;
  inCorso: boolean;
  onSalva: (v: { dal: string; al: string | null }) => void;
  onAnnulla: () => void;
}) {
  const [dal, setDal] = useState(dal0);
  const [al, setAl] = useState(al0 ?? '');
  const buono = dal !== '' && (al === '' || al >= dal);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (buono) onSalva({ dal, al: al === '' ? null : al });
      }}
    >
      <label className="campo">
        <span>Dal</span>
        <input type="date" value={dal} onChange={(e) => setDal(e.target.value)} />
      </label>
      <label className="campo">
        <span>Al (vuoto = fino a nuovo avviso)</span>
        <input type="date" value={al} min={dal} onChange={(e) => setAl(e.target.value)} />
      </label>
      <button type="submit" className="piccolo" disabled={!buono || inCorso}>
        Salva
      </button>{' '}
      <button type="button" className="bordato piccolo" onClick={onAnnulla}>
        Lascia stare
      </button>
    </form>
  );
}
